import React from 'react';

import {Anchor, IconButton} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {useGoogleClassroomSync} from 'client/components/ContentDiscovery/Document/AssignmentSharing/useGoogleClassroomSync';
import {withStopPropagation} from 'client/components/ContentDiscovery/Document/AssignmentSharing/utils';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import {SyncStatusOverlay} from '../lmsSync/SyncStatusOverlay';

const HELP_LINK_HREF =
  'https://help.albert.io/en/articles/1885029-how-do-i-sync-my-albert-classroom-with-a-google-classroom';
const CANNOT_SYNC_MESSAGE =
  'Cannot sync. Connect this classroom to a Google Classroom course first.';

const GC_SYNC_ICON: IconProp = ['fab', 'google'];

const GC_SYNC_ICON_BUTTON_PROPS = {
  icon: GC_SYNC_ICON,
  size: 's' as const,
  tooltipProps: {theme: 'dark', placement: 'bottom', showArrow: true} as const
};

interface Props {
  assignmentId: string;
  classroomId: string;
  /** Class is linked to a Google Classroom course; false → render the can't-sync tooltip. */
  classroomConnectedToGoogleClassroom: boolean;
  /** The teacher's Google Classroom connection per the backend; `undefined` while unknown. */
  googleClassroomConnected?: boolean;
  /** Seeds the initial synced state (useGoogleClassroomSync also follows later prop changes). */
  initialSynced: boolean;
}

/**
 * Per-assignment Google Classroom sync affordance for the v2 (class-scoped) assignments row —
 * mirrors AssignmentLtiSyncCell's stateful icon, reusing the builder's useGoogleClassroomSync hook
 * so the sync/unsync flow (including the not-connected reconnect prompt) is unchanged.
 */
export const AssignmentGoogleClassroomSyncCell = ({
  assignmentId,
  classroomId,
  classroomConnectedToGoogleClassroom,
  googleClassroomConnected,
  initialSynced
}: Props) => {
  const {isSynced, isRequestPending, needsReconnect, sync, unsync, reconnect} =
    useGoogleClassroomSync(assignmentId, classroomId, initialSynced, googleClassroomConnected);

  // useGoogleClassroomSync only busts the single-assignment interest (the builder's own read
  // path), not this row's class-scoped list query, so a remount (e.g. after switching classes and
  // back) would otherwise re-seed from the pre-sync cache. Bust that list explicitly on success.
  const refreshClassSyncStatus = () =>
    invalidatePartialInterest({resourcePath: ['classrooms_v1', classroomId, 'assignments_v3']});
  const syncAndRefresh = () => sync().then(refreshClassSyncStatus);
  const unsyncAndRefresh = () => unsync().then(refreshClassSyncStatus);

  if (!classroomConnectedToGoogleClassroom) {
    return (
      <IconButton
        {...GC_SYNC_ICON_BUTTON_PROPS}
        label='Cannot sync grades'
        className='relative'
        iconProps={{color: 'tertiary'}}
        onClick={withStopPropagation(() => {})}
        tooltip={
          <div>
            {`${CANNOT_SYNC_MESSAGE} `}
            <Anchor target='_blank' to={HELP_LINK_HREF}>
              Learn More
            </Anchor>
          </div>
        }
        tooltipProps={{theme: 'dark', placement: 'bottom', showArrow: true, interactive: true}}
      >
        <SyncStatusOverlay status='blocked' />
      </IconButton>
    );
  }

  if (needsReconnect) {
    return (
      <IconButton
        {...GC_SYNC_ICON_BUTTON_PROPS}
        label='Reconnect Google Classroom'
        className='relative'
        data-testid='google-classroom-reconnect-button'
        onClick={withStopPropagation(reconnect)}
        tooltip='Google Classroom is not connected, so grades are not syncing — click to reconnect'
        iconProps={{color: 'tertiary'}}
      >
        <SyncStatusOverlay status='error' />
      </IconButton>
    );
  }

  if (isSynced) {
    return (
      <IconButton
        {...GC_SYNC_ICON_BUTTON_PROPS}
        label='Synced with Google Classroom'
        className='relative'
        disabled={isRequestPending}
        onClick={withStopPropagation(unsyncAndRefresh)}
        tooltip='Synced with Google Classroom — click to unsync'
        iconProps={{spin: isRequestPending}}
      >
        <SyncStatusOverlay status='synced' />
      </IconButton>
    );
  }

  return (
    <IconButton
      {...GC_SYNC_ICON_BUTTON_PROPS}
      label='Sync with Google Classroom'
      disabled={isRequestPending}
      onClick={withStopPropagation(syncAndRefresh)}
      tooltip='Sync with Google Classroom'
      iconProps={{spin: isRequestPending}}
    />
  );
};

/**
 * Cross-class (All Classwork) variant: sync is per-class only, so this is a non-actionable icon
 * whose tooltip points the teacher to the per-class flow. Mirrors CrossClassLtiSyncCell. Shown only
 * when ≥1 class is linked to a Google Classroom course.
 */
export const CrossClassGoogleClassroomSyncCell = () => (
  // Informational only — rendered as a labelled `img` (not a button) so it's out of the tab order and
  // AT announces the guidance instead of a no-op command. Sync is per-class.
  <IconButton
    {...GC_SYNC_ICON_BUTTON_PROPS}
    as='span'
    role='img'
    label='Google Classroom sync is available per class'
    tooltip='To sync with Google Classroom, navigate to an individual class or open the assignment editor.'
  />
);
